/**
 * Prompts API slices (prompts, prompt categories).
 * Leaf module (Perf 3.1.b/e Step 5). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { fetchWithAuth, ApiError } from './client';

export const promptsApi = {
  list: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    return fetchWithAuth(`/json/prompts${qs ? `?${qs}` : ''}`);
  },

  getBySlug: async (slug) => {
    return fetchWithAuth(`/json/prompts/${encodeURIComponent(slug)}`);
  },

  related: async (slug, { limit = 8 } = {}) => {
    const qs = new URLSearchParams({ limit: String(limit) }).toString();
    return fetchWithAuth(`/json/prompts/${encodeURIComponent(slug)}/related?${qs}`);
  },

  // Tools the user can run for the same job as this recipe (matched on
  // intentAction / outputType). Drives the "Related tools" row on the prompt
  // detail page.
  relatedTools: async (slug, { limit = 8 } = {}) => {
    const qs = new URLSearchParams({ limit: String(limit) }).toString();
    return fetchWithAuth(`/json/prompts/${encodeURIComponent(slug)}/related-tools?${qs}`);
  },

  run: async (slug, { inputs = {}, modelId = null, fileIds = [], clientInfo = null } = {}) => {
    return fetchWithAuth(`/json/prompts/${encodeURIComponent(slug)}/run`, {
      method: 'POST',
      body: { inputs, modelId, fileIds, clientInfo },
    });
  },

  calculateCredits: async (slug, { inputs = {}, modelId = null, fileIds = [] } = {}) => {
    return fetchWithAuth(`/json/prompts/${encodeURIComponent(slug)}/calculate-credits`, {
      method: 'POST',
      body: { inputs, modelId, fileIds },
    });
  },

  favoritePrompt: async (promptId) => {
    const data = await fetchWithAuth(`/json/user/me/favorite-prompts/${encodeURIComponent(promptId)}`, {
      method: 'POST',
    });
    if (data.error) throw new ApiError(data.message || 'Failed to favorite prompt', data.status || 400, data);
    return !data.error;
  },

  unfavoritePrompt: async (promptId) => {
    const data = await fetchWithAuth(`/json/user/me/favorite-prompts/${encodeURIComponent(promptId)}`, {
      method: 'DELETE',
    });
    if (data.error) throw new ApiError(data.message || 'Failed to unfavorite prompt', data.status || 400, data);
    return !data.error;
  },

  trackView: async (slug) => {
    return fetchWithAuth(`/json/prompts/${encodeURIComponent(slug)}/view`, {
      method: 'POST',
    });
  },
};

export const promptCategoriesApi = {
  list: async () => fetchWithAuth('/json/prompt-categories'),
};
